import { WalletAvatar } from '@/components/Share/WalletAvatar'
import { AccountAllocation as AccountAllocationType } from '@/hooks/useAccountAllocationsData'
import { formatNumber, shortenAddress } from '@ourzora/utils'
import { ReactNode } from 'react'
import { Hidden, Icon, Info, Text, Tooltip, View } from 'reshaped'
import { Address } from 'viem'
import { Avatar } from './Avatar'
import { EmbeddedWalletClaim } from './EmbeddedWalletClaim'
import { EOAClaim } from './EOAClaim'
import { SmartWalletClaim } from './SmartWalletClaim'

function WalletAllocation({
  avatar,
  handle,
  walletType,
  tooltipText,
  action,
  tokens,
}: {
  avatar: ReactNode
  handle: string
  walletType: string
  tooltipText: string
  action: ReactNode
  tokens: number
}) {
  return (
    <View
      paddingInline={4}
      paddingTop={3}
      paddingBottom={3}
      borderRadius="large"
      borderColor="neutral-faded"
    >
      <Hidden hide={{ s: false, m: true }}>
        <View direction="row" align="center" justify="space-between" width="100%">
          <View direction="row" gap={{ s: 3, m: 2 }} align="center">
            {avatar}
            <View direction="column" gap={1}>
              <Text ellipsis color="primary" variant="body-3" weight="semibold">
                {handle}
              </Text>
              <View direction="row" gap={0.5}>
                <Text color="neutral-faded" variant="caption-1" weight="regular">
                  {walletType}
                </Text>
                <Tooltip text={tooltipText}>
                  {(attributes) => (
                    <Icon
                      svg={Info}
                      size={4}
                      color="neutral-faded"
                      attributes={attributes}
                    />
                  )}
                </Tooltip>
              </View>

              {tokens > 0 && (
                <Text color="positive" variant="body-3" weight="medium">
                  + {formatNumber(Math.floor(tokens))} $ZORA
                </Text>
              )}
            </View>
          </View>
          <View width="90px">{action}</View>
        </View>
      </Hidden>

      <Hidden hide={{ s: true, m: false }}>
        <View direction="row" align="center" justify="space-between" width="100%" gap={4}>
          <View direction="row" gap={2} align="center">
            {avatar}
            <View align="start">
              <Text ellipsis color="primary" variant="body-3" weight="semibold">
                {handle}
              </Text>
              <View direction="row" gap={0.5}>
                <Text color="neutral-faded" variant="caption-1" weight="regular">
                  {walletType}
                </Text>
                <Tooltip text={tooltipText}>
                  {(attributes) => (
                    <Icon
                      svg={Info}
                      size={4}
                      color="neutral-faded"
                      attributes={attributes}
                    />
                  )}
                </Tooltip>
              </View>
            </View>
          </View>

          <View direction="row" gap={8} align="center">
            {tokens > 0 && (
              <Text color="positive" variant="body-2" weight="medium">
                + {formatNumber(Math.floor(tokens))} $ZORA
              </Text>
            )}
            <View width="90px">{action}</View>
          </View>
        </View>
      </Hidden>
    </View>
  )
}

export function AccountAllocation({
  account,
  externalWalletAddress,
}: {
  account: AccountAllocationType
  externalWalletAddress?: Address
}) {
  return (
    <View gap={3} paddingBottom={6}>
      <View gap={{ s: 3, m: 4 }}>
        {account.externalWallet && account.externalWallet.tokens > 0 && (
          <WalletAllocation
            avatar={
              account.avatarKey && (
                <Avatar avatarKey={account.avatarKey} size={{ s: 16, m: 10 }} />
              )
            }
            handle={account.handle}
            walletType="External wallet"
            tooltipText="You created this using platforms like Rainbow, Zerion or MetaMask."
            tokens={account.externalWallet.tokens}
            action={
              <EOAClaim
                status={account.externalWallet.claimStatus}
                address={account.externalWallet.walletAddress}
                tokens={account.externalWallet.tokens}
                textVariant="body-2"
              />
            }
          />
        )}

        {account.embeddedWallet && account.embeddedWallet.tokens > 0 && (
          <WalletAllocation
            avatar={<WalletAvatar size={{ s: 16, m: 10 }} />}
            handle={shortenAddress(account.embeddedWallet.walletAddress)}
            walletType="Embedded wallet"
            tooltipText="Created when you first signed up on Zora."
            tokens={account.embeddedWallet.tokens}
            action={
              <EmbeddedWalletClaim
                status={account.embeddedWallet.claimStatus}
                address={account.embeddedWallet.walletAddress}
                smartWalletAddress={account.smartWallet?.walletAddress}
                tokens={account.embeddedWallet.tokens}
                textVariant="body-2"
                externalWalletAddress={externalWalletAddress}
              />
            }
          />
        )}

        {account.smartWallet && account.smartWallet.tokens > 0 && (
          <WalletAllocation
            avatar={<WalletAvatar size={{ s: 16, m: 10 }} />}
            handle={shortenAddress(account.smartWallet.walletAddress)}
            walletType="Zora smart wallet"
            tooltipText="Created for you when you used our iOS or Android app."
            tokens={account.smartWallet.tokens}
            action={
              <SmartWalletClaim
                status={account.smartWallet.claimStatus}
                smartWalletAddress={account.smartWallet.walletAddress}
                smartWalletOwners={account.smartWallet.owners}
                tokens={account.smartWallet.tokens}
                textVariant="body-2"
              />
            }
          />
        )}
      </View>
    </View>
  )
}
